import { Link, NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useHandoverSession, getWorkspaceDestinations } from "../../hooks/useHandoverSession";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_care-toolkit-pro/artifacts/xtaliuak_Just%20Care%20-%201.png";

// Routes that are unambiguously carer-facing or family-facing, so the
// header "Join free" CTA can route to the matching free tier instead of
// always defaulting to Carer Starter. Anything not matched here is a
// shared/general page — those get an explicit Carer/Family choice below
// instead of guessing.
const CARER_ROUTE_PREFIXES = [
  "/for-carers", "/pro-carer-circle", "/carer-profiles", "/shift-kit",
  "/independent-status-reality-check", "/join/carer-starter",
  "/workspace/carer-starter", "/workspace/carer-starter-hub",
  "/workspace/pro-carer-circle", "/workspace/pro-carer-profile",
  "/workspace/carer-profile", "/care-discussion-room/new-carer",
];
const FAMILY_ROUTE_PREFIXES = [
  "/for-families", "/family-access", "/family/", "/join/family-starter",
  "/workspace/family-starter", "/workspace/family-starter-hub",
  "/workspace/family-free", "/workspace/family-circle",
];

function audienceForPath(pathname) {
  if (CARER_ROUTE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"))) return "carer";
  if (FAMILY_ROUTE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p))) return "family";
  return null;
}

/**
 * Truncate an email for the navbar. The local-part is kept whole up to
 * `max` chars, then we insert an ellipsis before `@domain` so the domain
 * remains readable — this is what most users pattern-match on.
 */
function displayEmail(email, max = 24) {
  if (!email || email.length <= max) return email || "";
  const at = email.indexOf("@");
  if (at <= 0) return email.slice(0, max - 1) + "…";
  const local = email.slice(0, at);
  const domain = email.slice(at);
  const keep = Math.max(1, max - domain.length - 1);
  return local.slice(0, keep) + "…" + domain;
}

const navTriggerClass = "px-3 py-2 rounded-full text-sm whitespace-nowrap transition-colors text-ink-muted hover:text-ink hover:bg-slate-50 inline-flex items-center gap-1";
const navLinkClass = ({ isActive }) =>
  `px-3 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
    isActive ? "text-ink font-medium bg-slate-100" : "text-ink-muted hover:text-ink hover:bg-slate-50"
  }`;

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [mobileGroup, setMobileGroup] = useState({ carers: false, families: false, resources: false });
  const { user, logout: legacyLogout } = useAuth();
  const location = useLocation();
  const audience = audienceForPath(location.pathname);
  const joinFreeHref = audience === "family" ? "/join/family-starter" : "/join/carer-starter";
  const session = useHandoverSession();

  const legacyActive = !!user;
  const handoverActive = session.active;
  const loggedIn = legacyActive || handoverActive;
  const workspaceDestinations = handoverActive ? getWorkspaceDestinations(session.entitlements) : [];

  // Same entitlement-aware destinations as before — only the labels and
  // grouping in the menu changed, not where any of these routes go.
  const familyEntitled = handoverActive &&
    ["family_free", "family_circle"].some((e) => session.entitlements.includes(e));
  const careNeedsHref = familyEntitled ? "/family/care-needs" : "/care-needs/post";

  const carerEntitledNav = handoverActive && session.entitlements.includes("carer_free");
  const createProfileHref = carerEntitledNav ? "/workspace/pro-carer-profile" : "/carer-profile/post";

  const hasCarerEssentials = handoverActive && session.entitlements.includes("carer_essentials");
  const carerEssentialsHref = hasCarerEssentials ? "/workspace/carer-essentials" : "/membership/carer-essentials";

  const canBrowseCareNeeds = handoverActive && session.entitlements.includes("carer_free");
  const careNeedsBrowseHref = canBrowseCareNeeds
    ? "/workspace/pro-carer-circle/care-opportunities"
    : "/care-needs/browse";

  async function handleLogout() {
    // Revoke handover session (server clears the __Host- cookie).
    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/handover/logout`,
        { method: "POST", credentials: "include" }
      );
    } catch (_) {
      /* network failed — the reload below still returns to public state */
    }
    // Revoke any legacy AuthContext session too, so mixed states cannot linger.
    try {
      if (legacyActive && typeof legacyLogout === "function") await legacyLogout();
    } catch (_) {
      /* ignore */
    }
    // Return the user to a sensible public page. Full navigation guarantees
    // the freshly-cleared cookie state is honoured on the next request.
    try {
      window.location.assign("/");
    } catch (_) {
      window.location.reload();
    }
  }

  const emailLabel = handoverActive && session.email
    ? `Logged in as ${displayEmail(session.email)}`
    : "You are logged in";

  const toggleMobileGroup = (key) => setMobileGroup((g) => ({ ...g, [key]: !g[key] }));

  return (
    <header className="sticky top-0 z-50 print:hidden bg-white/90 backdrop-blur border-b border-slate-100" data-testid="site-navbar">
      <div className="w-full px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center gap-2 py-1 shrink-0" data-testid="nav-logo" aria-label="Just Care Community — home">
          <img
            src={LOGO_URL}
            alt="Just Care Community"
            className="h-10 md:h-12 w-auto object-contain select-none"
            draggable={false}
          />
        </Link>

        {/* Horizontal grouped menu — clear top-level links + small
            dropdowns for related topics only. Switches to the mobile
            "Menu" button below lg (1024px) rather than ever wrapping,
            clipping or shrinking labels. */}
        <nav className="hidden lg:flex items-center gap-0.5" data-testid="nav-desktop">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className={navTriggerClass} data-testid="nav-carers-trigger">
                For Carers <ChevronDown className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              <DropdownMenuItem asChild>
                <Link to="/for-carers" data-testid="nav-carers-overview">For Carers overview</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/carer-quiz" data-testid="nav-carers-quiz">Carer Reflection Quiz</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={createProfileHref} data-testid="nav-carers-create-profile">Create Your Carer Profile</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={careNeedsBrowseHref} data-testid="nav-carers-browse-care-needs">Browse Care Needs</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={carerEssentialsHref} data-testid="nav-carers-essentials">Carer Essentials</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/pro-carer-circle" data-testid="nav-carers-pro-office">Pro Carer Office</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className={navTriggerClass} data-testid="nav-families-trigger">
                For Families <ChevronDown className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              <DropdownMenuItem asChild>
                <Link to="/for-families" data-testid="nav-families-overview">For Families overview</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/carer-profiles" data-testid="nav-families-browse-profiles">Browse Carer Profiles</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={careNeedsHref} data-testid="nav-families-post-care-needs">Post Your Care Needs</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/family-access" data-testid="nav-families-care-office">Family Care Office</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <NavLink to="/compare-memberships" data-testid="nav-compare-memberships" className={navLinkClass}>
            Compare Memberships
          </NavLink>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className={navTriggerClass} data-testid="nav-resources-trigger">
                Resources <ChevronDown className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuItem asChild>
                <Link to="/resources" data-testid="nav-resources-library">Resource Library</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/shop" data-testid="nav-resources-shop">JCC Shop</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/wim-blog" data-testid="nav-resources-wim-blog">WIM Blog</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/care-news" data-testid="nav-resources-care-news">Care News</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/faq" data-testid="nav-resources-faq">FAQ</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/about" data-testid="nav-resources-about">About</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="hidden lg:flex items-center gap-1.5 shrink-0">
          {loggedIn ? (
            <>
              {workspaceDestinations.length === 1 && (
                <a
                  href={workspaceDestinations[0].href}
                  className="btn-ghost text-sm whitespace-nowrap"
                  data-testid="nav-my-workspace"
                >
                  My Workspace
                </a>
              )}
              {workspaceDestinations.length > 1 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="btn-ghost text-sm inline-flex items-center gap-1 whitespace-nowrap"
                      data-testid="nav-my-workspace"
                    >
                      My Workspace <ChevronDown className="w-3.5 h-3.5" strokeWidth={2} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {workspaceDestinations.map((d) => (
                      <DropdownMenuItem key={d.tier} asChild>
                        <a href={d.href} data-testid={`nav-my-workspace-${d.tier}`}>
                          {d.label}
                        </a>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {/* Email label deliberately never appears in this flat row —
                  My Workspace + Log out are the only things guaranteed
                  accessible here; the address is still shown in the
                  mobile panel below. */}
              <button
                type="button"
                onClick={handleLogout}
                className="btn-outline text-sm py-2 whitespace-nowrap"
                data-testid="nav-logout"
              >
                Log out
              </button>
            </>
          ) : (
            <>
              <a
                href="/auth/handover/request"
                className="btn-ghost text-sm whitespace-nowrap"
                data-testid="nav-login"
              >
                Log in
              </a>
              {audience ? (
                <a
                  href={joinFreeHref}
                  className="btn-teal text-sm py-2 whitespace-nowrap"
                  data-testid="nav-register"
                >
                  Join free
                </a>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="btn-teal text-sm py-2 inline-flex items-center gap-1 whitespace-nowrap"
                      data-testid="nav-register"
                    >
                      Join free <ChevronDown className="w-3.5 h-3.5" strokeWidth={2} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild>
                      <a href="/join/carer-starter" data-testid="nav-register-carer">I'm a Carer</a>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <a href="/join/family-starter" data-testid="nav-register-family">I'm a Family</a>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </>
          )}
        </div>

        <button
          className="lg:hidden p-2 rounded-full hover:bg-slate-100"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
          aria-expanded={open}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white max-h-[calc(100vh-4rem)] overflow-y-auto" data-testid="nav-mobile-panel">
          <div className="container-jcc py-4 flex flex-col gap-1">
            <button
              type="button"
              onClick={() => toggleMobileGroup("carers")}
              className="flex items-center justify-between px-3 py-2.5 rounded-lg text-ink font-medium hover:bg-slate-50"
              aria-expanded={mobileGroup.carers}
              data-testid="nav-mobile-carers-toggle"
            >
              For Carers <ChevronDown className={`w-4 h-4 transition-transform ${mobileGroup.carers ? "rotate-180" : ""}`} />
            </button>
            {mobileGroup.carers && (
              <div className="flex flex-col gap-1 pl-4 pb-2">
                <Link to="/for-carers" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm text-ink-muted hover:bg-slate-50" data-testid="nav-mobile-carers-overview">For Carers overview</Link>
                <Link to="/carer-quiz" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm text-ink-muted hover:bg-slate-50" data-testid="nav-mobile-carers-quiz">Carer Reflection Quiz</Link>
                <Link to={createProfileHref} onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm text-ink-muted hover:bg-slate-50" data-testid="nav-mobile-carers-create-profile">Create Your Carer Profile</Link>
                <Link to={careNeedsBrowseHref} onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm text-ink-muted hover:bg-slate-50" data-testid="nav-mobile-carers-browse-care-needs">Browse Care Needs</Link>
                <Link to={carerEssentialsHref} onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm text-ink-muted hover:bg-slate-50" data-testid="nav-mobile-carers-essentials">Carer Essentials</Link>
                <Link to="/pro-carer-circle" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm text-ink-muted hover:bg-slate-50" data-testid="nav-mobile-carers-pro-office">Pro Carer Office</Link>
              </div>
            )}

            <button
              type="button"
              onClick={() => toggleMobileGroup("families")}
              className="flex items-center justify-between px-3 py-2.5 rounded-lg text-ink font-medium hover:bg-slate-50"
              aria-expanded={mobileGroup.families}
              data-testid="nav-mobile-families-toggle"
            >
              For Families <ChevronDown className={`w-4 h-4 transition-transform ${mobileGroup.families ? "rotate-180" : ""}`} />
            </button>
            {mobileGroup.families && (
              <div className="flex flex-col gap-1 pl-4 pb-2">
                <Link to="/for-families" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm text-ink-muted hover:bg-slate-50" data-testid="nav-mobile-families-overview">For Families overview</Link>
                <Link to="/carer-profiles" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm text-ink-muted hover:bg-slate-50" data-testid="nav-mobile-families-browse-profiles">Browse Carer Profiles</Link>
                <Link to={careNeedsHref} onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm text-ink-muted hover:bg-slate-50" data-testid="nav-mobile-families-post-care-needs">Post Your Care Needs</Link>
                <Link to="/family-access" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm text-ink-muted hover:bg-slate-50" data-testid="nav-mobile-families-care-office">Family Care Office</Link>
              </div>
            )}

            <Link
              to="/compare-memberships"
              onClick={() => setOpen(false)}
              className="px-3 py-2.5 rounded-lg text-ink font-medium hover:bg-slate-50"
              data-testid="nav-mobile-compare-memberships"
            >
              Compare Memberships
            </Link>

            <button
              type="button"
              onClick={() => toggleMobileGroup("resources")}
              className="flex items-center justify-between px-3 py-2.5 rounded-lg text-ink font-medium hover:bg-slate-50"
              aria-expanded={mobileGroup.resources}
              data-testid="nav-mobile-resources-toggle"
            >
              Resources <ChevronDown className={`w-4 h-4 transition-transform ${mobileGroup.resources ? "rotate-180" : ""}`} />
            </button>
            {mobileGroup.resources && (
              <div className="flex flex-col gap-1 pl-4 pb-2">
                <Link to="/resources" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm text-ink-muted hover:bg-slate-50" data-testid="nav-mobile-resources-library">Resource Library</Link>
                <Link to="/shop" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm text-ink-muted hover:bg-slate-50" data-testid="nav-mobile-resources-shop">JCC Shop</Link>
                <Link to="/wim-blog" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm text-ink-muted hover:bg-slate-50" data-testid="nav-mobile-resources-wim-blog">WIM Blog</Link>
                <Link to="/care-news" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm text-ink-muted hover:bg-slate-50" data-testid="nav-mobile-resources-care-news">Care News</Link>
                <Link to="/faq" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm text-ink-muted hover:bg-slate-50" data-testid="nav-mobile-resources-faq">FAQ</Link>
                <Link to="/about" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm text-ink-muted hover:bg-slate-50" data-testid="nav-mobile-resources-about">About</Link>
              </div>
            )}

            <div className="pt-3 border-t border-slate-100 mt-2">
              {loggedIn ? (
                <div className="flex flex-col gap-2">
                  {workspaceDestinations.map((d) => (
                    <a
                      key={d.tier}
                      href={d.href}
                      onClick={() => setOpen(false)}
                      className="btn-outline text-sm text-center"
                      data-testid={
                        workspaceDestinations.length > 1
                          ? `nav-mobile-my-workspace-${d.tier}`
                          : "nav-mobile-my-workspace"
                      }
                    >
                      {workspaceDestinations.length > 1 ? d.label : "My Workspace"}
                    </a>
                  ))}
                  <div className="flex items-center gap-3">
                    <span
                      className="text-sm text-ink-muted flex-1 truncate"
                      title={session.email || undefined}
                      data-testid="nav-mobile-user-label"
                    >
                      {emailLabel}
                    </span>
                    <button
                      type="button"
                      onClick={() => { setOpen(false); handleLogout(); }}
                      className="btn-ghost text-sm"
                      data-testid="nav-mobile-logout"
                    >
                      Log out
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <a
                    href="/auth/handover/request"
                    onClick={() => setOpen(false)}
                    className="btn-outline text-sm text-center"
                    data-testid="nav-mobile-login"
                  >
                    Log in
                  </a>
                  {audience ? (
                    <a
                      href={joinFreeHref}
                      onClick={() => setOpen(false)}
                      className="btn-teal text-sm text-center"
                      data-testid="nav-mobile-register"
                    >
                      Join free
                    </a>
                  ) : (
                    <div className="flex gap-2">
                      <a
                        href="/join/carer-starter"
                        onClick={() => setOpen(false)}
                        className="btn-teal text-sm flex-1 text-center"
                        data-testid="nav-mobile-register-carer"
                      >
                        I'm a Carer
                      </a>
                      <a
                        href="/join/family-starter"
                        onClick={() => setOpen(false)}
                        className="btn-teal text-sm flex-1 text-center"
                        data-testid="nav-mobile-register-family"
                      >
                        I'm a Family
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
